import Layout from "@/components/layouts/Layout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Shield, Eye, Database, UserCheck, Mail, FileText } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const PrivacyPolicy = () => {
  const { t, language } = useLanguage();

  const getArrayTranslation = (key: string): string[] => {
    const translation = t(key);
    return Array.isArray(translation) ? translation : [];
  };

  const ListItem = ({ text }: { text: string }) => (
    <li className="flex items-start gap-3">
      <div className="w-2 h-2 bg-nexus-teal rounded-full mt-2 flex-shrink-0" />
      <span className="text-gray-600 dark:text-gray-400">{text}</span>
    </li>
  );

  const BulletListItem = ({ text }: { text: string }) => (
    <li className="text-gray-600 dark:text-gray-400">• {text}</li>
  );

  return (
    <Layout>
      <div className="bg-gradient-to-b from-nexus-teal/5 to-gray-300 py-16">
        <div className="container">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-nexus-teal/10 rounded-lg flex items-center justify-center mx-auto mb-6">
              <Shield className="h-8 w-8 text-nexus-teal" />
            </div>
            <h1 className="text-4xl font-bold text-nexus-gray dark:text-gray-200 mb-4">
              {t("privacyPolicy.title")}
            </h1>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t("privacyPolicy.description")}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-300 mt-4">
              {t("privacyPolicy.lastUpdated")}: {new Date().toLocaleDateString(language)}
            </p>
          </div>

          {/* Content */}
          <div className="max-w-4xl mx-auto space-y-8">
            {/* Information We Collect */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Database className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.informationCollection.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <h3 className="font-semibold mb-2">
                    {t("privacyPolicy.sections.informationCollection.personalInfo.title")}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("privacyPolicy.sections.informationCollection.personalInfo.content")}
                  </p>
                </div>
                <Separator />
                <div>
                  <h3 className="font-semibold mb-2">
                    {t("privacyPolicy.sections.informationCollection.usageInfo.title")}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {t("privacyPolicy.sections.informationCollection.usageInfo.content")}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* How We Use Your Information */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Eye className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.informationUse.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {getArrayTranslation("privacyPolicy.sections.informationUse.items").map(
                    (item, index) => (
                      <ListItem key={index} text={item} />
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Information Sharing */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <UserCheck className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.informationSharing.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600">
                  {t("privacyPolicy.sections.informationSharing.intro")}
                </p>
                <ul className="space-y-2 ml-4">
                  {getArrayTranslation("privacyPolicy.sections.informationSharing.items").map(
                    (item, index) => (
                      <BulletListItem key={index} text={item} />
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Data Security */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Shield className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.dataSecurity.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("privacyPolicy.sections.dataSecurity.content")}
                </p>
              </CardContent>
            </Card>

            {/* Your Rights */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <FileText className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.yourRights.title")}
                </h2>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-gray-600 dark:text-gray-400">
                  {t("privacyPolicy.sections.yourRights.intro")}
                </p>
                <ul className="space-y-2 ml-4">
                  {getArrayTranslation("privacyPolicy.sections.yourRights.items").map(
                    (item, index) => (
                      <BulletListItem key={index} text={item} />
                    )
                  )}
                </ul>
              </CardContent>
            </Card>

            {/* Contact Information */}
            <Card>
              <CardHeader>
                <h2 className="flex items-center gap-3 text-2xl font-semibold leading-none tracking-tight">
                  <Mail className="h-5 w-5 text-nexus-teal" />
                  {t("privacyPolicy.sections.contact.title")}
                </h2>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 dark:text-gray-400">
                  {t("privacyPolicy.sections.contact.content")}
                </p>
                <div className="mt-4 p-4 bg-gray-50 rounded-lg">
                  <p className="text-gray-700">
                    {t("privacyPolicy.sections.contact.details.email")}<br />
                    {t("privacyPolicy.sections.contact.details.address")}<br />
                    {t("privacyPolicy.sections.contact.details.businessAddress")}
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default PrivacyPolicy;
